import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { ChevronLeft, Clock, AlertTriangle, X } from "lucide-react";
import { getPendingByRegistrationId, updateRegistrationFiscalData, getClassesForForum, updateRegistrationClasses, updateRegistrationPaymentMethod } from "../lib/api";
import type { FiscalData, DocumentType, ForumClassFromApi } from "../types";
import { useAuth } from "../contexts/AuthContext";

const LANG_STORAGE_KEY = "forumLang";
function useLang(): "es" | "en" {
  try {
    const s = localStorage.getItem(LANG_STORAGE_KEY);
    return s === "en" ? "en" : "es";
  } catch (_) {
    return "es";
  }
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP", minimumFractionDigits: 2 })
    .format(amount)
    .replace("DOP", "RD$ ");

const MONTH_SHORT = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];

/** Format YYYY-MM to Spanish month and year (e.g. "marzo 2026"). */
function formatMonth(ym: string): string {
  const [y, m] = ym.split("-");
  const monthIndex = parseInt(m, 10) - 1;
  if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) return ym;
  const date = new Date(parseInt(y, 10), monthIndex, 1);
  return date.toLocaleDateString("es-DO", { month: "long", year: "numeric" });
}

/**
 * Returns fiscal period string: "23 abr – 26 may 2026"
 * The school's billing period runs from the 23rd of the registration month
 * to the 26th of the following month.
 */
function formatFiscalPeriod(ym: string): string {
  const parts = ym.split("-");
  if (parts.length !== 2) return ym;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // 0-indexed
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  return `23 ${MONTH_SHORT[month]} – 26 ${MONTH_SHORT[nextMonth]} ${nextYear}`;
}

/** Number of months to charge based on frequency */
function freqMonths(freq: string): number {
  return freq === "CUATRIMESTRAL" ? 4 : freq === "BIMESTRAL" ? 2 : 1;
}
/** Discount factor (cuatrimestral = 5% off) */
function freqDiscount(freq: string): number {
  return freq === "CUATRIMESTRAL" ? 0.95 : 1.0;
}

const defaultFiscalForm = (existing?: FiscalData): FiscalData => ({
  fiscalName: existing?.fiscalName ?? "",
  documentType: (existing?.documentType ?? "RNC") as DocumentType,
  documentNumber: existing?.documentNumber ?? "",
  // comprobanteType when user says "Sí" is always B01 (Crédito Fiscal)
  comprobanteType: "B01",
  requireNCF: true,
});

export function PagosByRegistrationId() {
  const { registrationId } = useParams<{ registrationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = useLang();

  const T = {
    back: lang === "es" ? "Volver" : "Back",
    loading: lang === "es" ? "Cargando…" : "Loading…",
    missingId: lang === "es" ? "Falta la matrícula." : "Student ID is missing.",
    checkId:
      lang === "es"
        ? "Comprueba que la matrícula sea correcta."
        : "Please check that the student ID is correct.",
    loadError:
      lang === "es"
        ? "No se pudo cargar. Revisa la matrícula."
        : "Could not load. Please check the student ID.",
    myClasses: lang === "es" ? "Mis clases" : "My classes",
    pendingSection:
      lang === "es" ? "Clases pendientes de pago" : "Classes pending payment",
    fullClassAlert:
      lang === "es"
        ? "Una o más clases han alcanzado el límite de cupos. Modifica tu selección antes de pagar."
        : "One or more classes have reached capacity. Modify your selection before paying.",
    noPendingClasses:
      lang === "es"
        ? "No tienes clases pendientes de pago."
        : "You have no classes pending payment.",
    noPaymentDue:
      lang === "es"
        ? "No tienes pagos pendientes en este momento."
        : "You have no pending payments at this time.",
    freqQuarterly: lang === "es" ? "Cuatrimestral · 5% desc" : "Quarterly · 5% off",
    freqBimonthly: lang === "es" ? "Bimestral · 2 meses" : "Bimonthly · 2 months",
    totalDue: lang === "es" ? "Total a pagar" : "Total due",
    discount5: lang === "es" ? "5% descuento" : "5% discount",
    retryPayment: lang === "es" ? "Intentar pagar de nuevo" : "Retry payment",
    payEnrollment: lang === "es" ? "Pagar inscripción" : "Pay enrollment",
    currentClasses: lang === "es" ? "Clases actuales" : "Current classes",
    enrolled: lang === "es" ? "Inscrito" : "Enrolled",
    noActiveClasses:
      lang === "es" ? "Aún no tienes clases activas" : "No active classes yet",
    noActiveClassesSub:
      lang === "es"
        ? "Aparecerán aquí una vez procesado el pago."
        : "They will appear here once payment is processed.",
    exploreClasses: lang === "es" ? "Explorar clases" : "Explore classes",
    viewAvailable:
      lang === "es" ? "Ver clases disponibles" : "View available classes",
    classSelectionClosed:
      lang === "es"
        ? "La selección de clases no se encuentra disponible."
        : "Class selection is not available.",
    removeClass: lang === "es" ? "Quitar clase" : "Remove class",
    editClassTitle:
      lang === "es" ? "Modificar selección de clases" : "Edit class selection",
    loadingClasses: lang === "es" ? "Cargando clases…" : "Loading classes…",
    classesHint:
      lang === "es"
        ? "Puedes marcar o desmarcar las clases con cupos habilitados."
        : "You can select or deselect classes with available spots.",
    selectNone:
      lang === "es" ? "Seleccionar ninguna clase" : "Select no classes",
    saveSelection: lang === "es" ? "Guardar selección" : "Save selection",
    saving: lang === "es" ? "Guardando…" : "Saving…",
    cancel: lang === "es" ? "Cancelar" : "Cancel",
    classDetailTitle: lang === "es" ? "Detalle de la clase" : "Class details",
    spots: lang === "es" ? "Cupos:" : "Spots:",
    paymentFrequency: lang === "es" ? "Frecuencia de pago:" : "Payment frequency:",
    freqBimonthlyLabel: lang === "es" ? "Bimensual" : "Bimonthly",
    freqQuarterlyLabel: lang === "es" ? "Cuatrimestral" : "Quarterly",
    close: lang === "es" ? "Cerrar" : "Close",
    switchOnlineTitle:
      lang === "es"
        ? "¿Desea realizar su pago en línea?"
        : "Would you like to pay online?",
    switchOnlineBody:
      lang === "es"
        ? "Esta inscripción fue marcada para pago manual. Puede cambiar el método de pago y continuar con el pago en línea."
        : "This enrollment was marked for manual payment. You can change the payment method and continue with online payment.",
    switchYes: lang === "es" ? "Sí, pagar en línea" : "Yes, pay online",
    switching: lang === "es" ? "Cambiando…" : "Changing…",
    no: lang === "es" ? "No" : "No",
    switchError:
      lang === "es"
        ? "No se pudo cambiar. Intente de nuevo."
        : "Could not switch. Please try again.",
    fiscalTitle:
      lang === "es" ? "¿Desea comprobante fiscal?" : "Would you like a fiscal receipt?",
    fiscalYes: lang === "es" ? "Sí" : "Yes",
    fiscalNo: lang === "es" ? "No" : "No",
    fiscalClose: lang === "es" ? "Cerrar" : "Close",
    fiscalB01Note:
      lang === "es"
        ? "Se emitirá una factura con valor fiscal (B01 — Crédito Fiscal)."
        : "A fiscal receipt will be issued (B01 — Tax Credit).",
    fiscalName: lang === "es" ? "Nombre fiscal" : "Fiscal name",
    fiscalNamePlaceholder:
      lang === "es" ? "Razón social o nombre" : "Business name or full name",
    documentType: lang === "es" ? "Tipo documento" : "Document type",
    documentNumber: lang === "es" ? "Número" : "Number",
    documentNumberPlaceholder: lang === "es" ? "RNC o cédula" : "RNC or ID number",
    fiscalNameError:
      lang === "es" ? "Ingresa el nombre fiscal." : "Enter the fiscal name.",
    fiscalDocError:
      lang === "es"
        ? "Crédito Fiscal (B01) requiere número de RNC o cédula."
        : "Tax Credit (B01) requires a RNC or ID number.",
    fiscalSaveError:
      lang === "es"
        ? "No se pudo guardar. Intente de nuevo."
        : "Could not save. Please try again.",
    continueToPay: lang === "es" ? "Continuar al pago" : "Continue to payment",
    backInModal: lang === "es" ? "Volver" : "Back",
    saveError: lang === "es" ? "No se pudo guardar." : "Could not save.",
    loadClassesError:
      lang === "es"
        ? "No se pudieron cargar las clases."
        : "Could not load classes.",
    perMonth: lang === "es" ? "/mes" : "/mo",
  };
  // If the user is logged in they came from the parent portal — go back there.
  // Otherwise (public payment link) go back to the home/registration page.
  const backTo = user ? "/parent/dashboard" : "/";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [childName, setChildName] = useState<string | null>(null);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [canPay, setCanPay] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [currentClasses, setCurrentClasses] = useState<Array<{ id: string; name: string }>>([]);
  const [pendingClasses, setPendingClasses] = useState<Array<{ id: string; name: string }>>([]);
  const [canEditClasses, setCanEditClasses] = useState(false);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [fiscalData, setFiscalData] = useState<FiscalData | undefined>(undefined);
  const [resolvedRegistrationId, setResolvedRegistrationId] = useState<string | null>(null);

  const [editingClasses, setEditingClasses] = useState(false);
  const [forumClasses, setForumClasses] = useState<ForumClassFromApi[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);
  const [savingClasses, setSavingClasses] = useState(false);
  const [classesError, setClassesError] = useState<string | null>(null);

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [comprobanteChoice, setComprobanteChoice] = useState<"ask" | "no" | "yes">("ask");
  const [fiscalForm, setFiscalForm] = useState<FiscalData>(() => defaultFiscalForm());
  const [savingFiscal, setSavingFiscal] = useState(false);
  const [fiscalError, setFiscalError] = useState<string | null>(null);

  const [pendingClassCapacities, setPendingClassCapacities] = useState<Record<string, { currentEnrollment: number; maxCapacity: number }>>({});
  const [classSelectionOpen, setClassSelectionOpen] = useState(false);
  const [paymentFrequency, setPaymentFrequency] = useState<"BIMESTRAL" | "CUATRIMESTRAL">("BIMESTRAL");
  const [classDetailId, setClassDetailId] = useState<string | null>(null);
  const [classDetailLoading, setClassDetailLoading] = useState(false);
  const [pendingMonth, setPendingMonth] = useState<string | null>(null);
  const [removingClassId, setRemovingClassId] = useState<string | null>(null);
  const [studentCode, setStudentCode] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"ONLINE" | "LOCAL">("ONLINE");
  const [switchToOnlineModalOpen, setSwitchToOnlineModalOpen] = useState(false);
  const [switchingPaymentMethod, setSwitchingPaymentMethod] = useState(false);
  const [switchPaymentError, setSwitchPaymentError] = useState<string | null>(null);

  // Warn authenticated parents before closing/refreshing — closing the tab ends their session.
  useEffect(() => {
    if (!user) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [user]);

  const id = registrationId ?? "";
  const payRegistrationId = resolvedRegistrationId ?? id;
  const pagosCode = studentCode ?? id;
  const hasPendingClasses = canPay && pendingClasses.length > 0;

  const hasFullClass = useMemo(() => {
    return pendingClasses.some((c) => {
      const cap = pendingClassCapacities[c.id];
      if (!cap || cap.maxCapacity <= 0) return false;
      return cap.currentEnrollment >= cap.maxCapacity;
    });
  }, [pendingClasses, pendingClassCapacities]);

  const openPayModal = () => {
    setFiscalError(null);
    setSwitchPaymentError(null);
    if (paymentMethod === "LOCAL") {
      setSwitchToOnlineModalOpen(true);
      return;
    }
    setComprobanteChoice("ask");
    setFiscalForm(defaultFiscalForm(fiscalData));
    setPayModalOpen(true);
  };

  const handleSwitchToOnlineYes = async () => {
    setSwitchPaymentError(null);
    setSwitchingPaymentMethod(true);
    try {
      await updateRegistrationPaymentMethod({ registrationId: payRegistrationId });
      setPaymentMethod("ONLINE");
      setSwitchToOnlineModalOpen(false);
      setComprobanteChoice("ask");
      setFiscalForm(defaultFiscalForm(fiscalData));
      setPayModalOpen(true);
    } catch (err) {
      setSwitchPaymentError(err instanceof Error ? err.message : T.switchError);
    } finally {
      setSwitchingPaymentMethod(false);
    }
  };

  const handleSwitchToOnlineNo = () => {
    setSwitchToOnlineModalOpen(false);
    setSwitchPaymentError(null);
  };

  const goToPay = () => {
    setPayModalOpen(false);
    const payUrl = pagosCode
      ? `/pay/by-id/${encodeURIComponent(payRegistrationId)}?returnCode=${encodeURIComponent(pagosCode)}`
      : `/pay/by-id/${encodeURIComponent(payRegistrationId)}`;
    navigate(payUrl);
  };

  const handleComprobanteNo = () => {
    goToPay();
  };

  const openClassEditor = () => {
    // Allow editing when payment is pending (canEditClasses) even if admin class window is closed
    if (!classSelectionOpen && !canEditClasses) return;
    setClassesError(null);
    setLocalSelectedIds(canEditClasses ? selectedClassIds : currentClasses.map((c) => c.id));
    setEditingClasses(true);
    if (forumClasses.length === 0) {
      setClassesLoading(true);
      getClassesForForum()
        .then((res) => setForumClasses(res.classes))
        .catch((err) => setClassesError(err instanceof Error ? err.message : T.loadClassesError))
        .finally(() => setClassesLoading(false));
    }
  };

  const toggleClassInEditor = (classId: string) => {
    const capacity = forumClasses.find((c) => c.id === classId);
    const cap = capacity?.maxCapacity ?? capacity?.cupos;
    const current = capacity?.currentEnrollment ?? 0;
    const isFull = cap != null && cap > 0 && current >= cap;
    const isSelected = localSelectedIds.includes(classId);
    if (isFull && !isSelected) return;
    setLocalSelectedIds((prev) =>
      prev.includes(classId) ? prev.filter((id) => id !== classId) : [...prev, classId]
    );
  };

  const saveClassSelection = async () => {
    setClassesError(null);
    setSavingClasses(true);
    try {
      const res = await updateRegistrationClasses({
        registrationId: payRegistrationId,
        selectedClasses: localSelectedIds,
      });
      setTotalPrice(res.totalPrice);
      setSelectedClassIds(localSelectedIds);
      setPendingClasses(
        localSelectedIds.map((id) => {
          const cls = forumClasses.find((c) => c.id === id);
          return { id, name: cls?.name ?? id };
        })
      );
      setEditingClasses(false);
    } catch (err) {
      setClassesError(err instanceof Error ? err.message : T.saveError);
    } finally {
      setSavingClasses(false);
    }
  };

  const removePendingClass = async (classId: string) => {
    if (!canEditClasses) return;
    const newIds = selectedClassIds.filter((id) => id !== classId);
    setRemovingClassId(classId);
    try {
      const res = await updateRegistrationClasses({
        registrationId: payRegistrationId,
        selectedClasses: newIds,
      });
      setTotalPrice(res.totalPrice);
      setSelectedClassIds(newIds);
      setPendingClasses((prev) => prev.filter((c) => c.id !== classId));
    } catch {
      // Ignore: user can use "Ver clases disponibles" to edit
    } finally {
      setRemovingClassId(null);
    }
  };

  const sortedForumClasses = useMemo(() => {
    return [...forumClasses].sort((a, b) =>
      (a.nameEs ?? a.nameEn ?? a.name).localeCompare(b.nameEs ?? b.nameEn ?? b.name, "es")
    );
  }, [forumClasses]);

  const handleComprobanteYesContinue = async () => {
    const name = fiscalForm.fiscalName.trim();
    const docNum = fiscalForm.documentNumber.trim();
    if (!name) {
      setFiscalError(T.fiscalNameError);
      return;
    }
    if (!docNum) {
      setFiscalError(T.fiscalDocError);
      return;
    }
    setFiscalError(null);
    setSavingFiscal(true);
    try {
      await updateRegistrationFiscalData({
        registrationId: payRegistrationId,
        fiscalData: {
          fiscalName: name,
          documentType: fiscalForm.documentType,
          documentNumber: docNum,
          comprobanteType: "B01",
          requireNCF: true,
        },
      });
      goToPay();
    } catch (err) {
      setFiscalError(err instanceof Error ? err.message : T.fiscalSaveError);
    } finally {
      setSavingFiscal(false);
    }
  };

  useEffect(() => {
    if (!id) {
      setError(T.missingId);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await getPendingByRegistrationId(id);
        setResolvedRegistrationId(res.registrationId);
        setChildName(res.childName);
        setTotalPrice(res.totalPrice);
        setCanPay(res.canPay);
        setMessage(res.message ?? null);
        setCurrentClasses(res.currentClasses ?? []);
        setPendingClasses(res.pendingClasses ?? []);
        setCanEditClasses(res.canEditClasses ?? false);
        setSelectedClassIds(res.selectedClasses ?? []);
        setFiscalData(res.fiscalData);
        setClassSelectionOpen(res.classSelectionOpen ?? false);
        setPaymentFrequency(res.paymentFrequency ?? "BIMESTRAL");
        setPendingMonth(res.pendingMonth ?? null);
        setStudentCode(res.studentCode ?? null);
        setPaymentMethod((res.paymentMethod === "LOCAL" ? "LOCAL" : "ONLINE") as "ONLINE" | "LOCAL");
      } catch (err) {
        setError(err instanceof Error ? err.message : T.loadError);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Keep URL as student code (canonical) so "back" from payment stays correct
  useEffect(() => {
    if (!studentCode || id === studentCode || loading) return;
    navigate(`/pagos/${encodeURIComponent(studentCode)}`, { replace: true });
  }, [studentCode, id, loading, navigate]);

  const pendingClassIdsKey = pendingClasses.map((c) => c.id).sort().join(",");
  useEffect(() => {
    if (!canPay || pendingClasses.length === 0) {
      setPendingClassCapacities({});
      return;
    }
    getClassesForForum()
      .then((res) => {
        setForumClasses(res.classes);
        const map: Record<string, { currentEnrollment: number; maxCapacity: number }> = {};
        res.classes.forEach((c) => {
          const cap = c.maxCapacity ?? c.cupos ?? 0;
          if (cap > 0) {
            map[c.id] = { currentEnrollment: c.currentEnrollment ?? 0, maxCapacity: cap };
          }
        });
        setPendingClassCapacities(map);
      })
      .catch(() => setPendingClassCapacities({}));
  }, [canPay, pendingClassIdsKey]);

  if (!id) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-[15px] font-medium text-brand-navy">{T.missingId}</p>
          <Link to={backTo} className="mt-5 inline-block text-[15px] font-semibold text-brand-navy hover:opacity-80 transition-opacity">
            {T.back}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-navy/20 border-t-brand-navy mx-auto" />
          <p className="mt-4 text-[14px] text-gray-400 font-medium">{T.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <p className="text-[15px] font-medium text-brand-navy">{error}</p>
          <p className="mt-2 text-[13px] text-gray-400">{T.checkId}</p>
          <Link to={backTo} className="mt-8 inline-block px-8 py-3.5 rounded-2xl font-semibold text-[14px] text-white bg-brand-navy hover:opacity-95 active:opacity-90 transition-opacity">
            {T.back}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-brand-bg p-4"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
    >
      <nav className="mb-3">
        <Link to={backTo} className="flex items-center gap-2 text-brand-navy font-semibold hover:opacity-70 transition-opacity group text-[13px]">
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>{T.back}</span>
        </Link>
      </nav>

      <div className="max-w-lg mx-auto">
        {/* Main Container Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] p-5 border border-black/[0.01]"
        >
          <header className="mb-4 flex flex-col gap-0 leading-tight">
            <h1 className="text-lg font-bold tracking-tight text-brand-navy">{T.myClasses}</h1>
            {childName && (
              <p className="text-gray-500 text-[13px] font-medium -mt-0.5">{childName}</p>
            )}
          </header>

          <div className="space-y-4">
            {/* Pending Section */}
            <section className="bg-brand-navy/[0.03] -mx-5 px-5 py-3 border-y border-brand-navy/5">
              <h2 className="text-[9px] font-bold tracking-widest text-brand-navy/60 uppercase mb-2.5">
                {T.pendingSection}
              </h2>

              {canPay ? (
                <div className="space-y-4">
                  {hasFullClass && (
                    <div className="rounded-lg border border-brand-maroon/25 bg-brand-maroon/5 px-3 py-2 flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-brand-maroon shrink-0 mt-0.5" />
                      <p className="text-[11px] font-medium text-brand-navy">
                        {T.fullClassAlert}
                      </p>
                    </div>
                  )}
                  {hasPendingClasses && (
                    <>
                      <div className="space-y-1.5">
                        {pendingClasses.map((item) => {
                          const forumClass = forumClasses.find((c) => c.id === item.id);
                          const monthlyPrice = forumClass?.monthlyPrice ?? 0;
                          const price = Math.round(monthlyPrice * freqMonths(paymentFrequency) * freqDiscount(paymentFrequency) * 100) / 100;
                          const displayName = forumClass?.nameEs ?? forumClass?.nameEn ?? forumClass?.name ?? item.name;
                          const isRemoving = removingClassId === item.id;
                          return (
                            <div key={item.id} className="flex items-center gap-2 bg-white p-2.5 rounded-lg border border-brand-navy/5 shadow-sm">
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] text-brand-navy font-bold leading-tight">
                                  {displayName}
                                </p>
                                {pendingMonth && (
                                  <p className="text-[11px] text-brand-navy/60 mt-0.5">
                                    {formatFiscalPeriod(pendingMonth)}
                                  </p>
                                )}
                              </div>
                              <span className="text-[13px] font-bold text-brand-navy tabular-nums shrink-0">
                                {price > 0 ? formatCurrency(price) : "—"}
                              </span>
                              {canEditClasses && (
                                <button
                                  type="button"
                                  onClick={() => removePendingClass(item.id)}
                                  disabled={isRemoving}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-brand-maroon hover:bg-brand-maroon/10 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                  aria-label={T.removeClass}
                                >
                                  {isRemoving ? (
                                    <span className="inline-block w-4 h-4 border-2 border-brand-navy/30 border-t-brand-navy rounded-full animate-spin" />
                                  ) : (
                                    <X className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className="pt-4 mt-1 border-t border-brand-navy/10 flex justify-between items-center">
                        <div className="flex flex-col">
                          <span className="text-[9px] font-bold text-brand-navy/40 uppercase tracking-[0.2em] mb-0.5">
                            {paymentFrequency === "CUATRIMESTRAL"
                              ? T.freqQuarterly
                              : T.freqBimonthly}
                          </span>
                          <span className="text-[13px] font-bold text-brand-navy">{T.totalDue}</span>
                        </div>
                        <div className="flex flex-col items-end">
                          {paymentFrequency === "CUATRIMESTRAL" && (
                            <span className="text-[10px] font-semibold text-green-600 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded-full mb-0.5">
                              {T.discount5}
                            </span>
                          )}
                          <span className="text-xl font-black text-brand-navy tabular-nums tracking-tight">
                            {formatCurrency(totalPrice)}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={openPayModal}
                        disabled={hasFullClass}
                        className="w-full bg-brand-navy hover:bg-brand-navy/95 text-white font-bold py-2.5 text-[13px] rounded-xl transition-all active:scale-[0.99] shadow-lg shadow-brand-navy/20 tracking-tight disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                      >
                        {message === "declined" ? T.retryPayment : T.payEnrollment}
                      </button>
                    </>
                  )}
                  {canPay && !hasPendingClasses && (
                    <p className="text-[12px] text-gray-500">{T.noPendingClasses}</p>
                  )}
                </div>
              ) : (
                <p className="text-[12px] text-gray-500 leading-snug">
                  {T.noPaymentDue}
                </p>
              )}
            </section>

            {/* Current Classes Section */}
            <section className="px-0.5">
              <h2 className="text-[9px] font-bold tracking-widest text-gray-400 uppercase mb-2.5">
                {T.currentClasses}
              </h2>
              {currentClasses.length > 0 ? (
                <ul className="space-y-1.5 pl-0 list-none">
                  {(() => {
                    const now = new Date();
                    const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
                    const monthLabel = formatMonth(pendingMonth || currentMonthStr);
                    return currentClasses.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setClassDetailId(c.id);
                            if (forumClasses.length === 0) {
                              setClassDetailLoading(true);
                              getClassesForForum()
                                .then((res) => setForumClasses(res.classes))
                                .finally(() => setClassDetailLoading(false));
                            }
                          }}
                          className="w-full flex items-center justify-between py-2 px-2.5 rounded-lg bg-gray-50/50 border border-gray-200 hover:border-brand-navy/20 hover:bg-gray-50 transition-colors text-left"
                        >
                          <span className="text-[12px] font-semibold text-gray-700 truncate">
                            {c.name} – {monthLabel}
                          </span>
                          <span className="text-[9px] font-semibold text-green-600 uppercase tracking-wide shrink-0">{T.enrolled}</span>
                        </button>
                      </li>
                    ));
                  })()}
                </ul>
              ) : (
                <div className="bg-gray-50/50 rounded-lg p-2.5 border border-dashed border-gray-200 flex flex-col items-center text-center">
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm mb-1">
                    <Clock className="w-3 h-3 text-gray-300" />
                  </div>
                  <p className="text-gray-600 font-semibold text-[12px] mb-0.5">{T.noActiveClasses}</p>
                  <p className="text-gray-400 font-medium text-[10px] max-w-[200px] leading-snug">
                    {T.noActiveClassesSub}
                  </p>
                </div>
              )}
            </section>

            <div className="h-px bg-gray-100 w-full" />

            {/* Explore Section */}
            <section className="px-0.5">
              <h2 className="text-[9px] font-bold tracking-widest text-gray-400 uppercase mb-2">
                {T.exploreClasses}
              </h2>
              <button
                type="button"
                onClick={openClassEditor}
                disabled={!classSelectionOpen && !canEditClasses}
                className="w-full bg-gray-50 hover:bg-gray-100 text-gray-500 font-bold py-2 rounded-xl transition-all active:scale-[0.99] text-[12px] border border-gray-200/50 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:bg-gray-50"
              >
                {T.viewAvailable}
              </button>
              {!classSelectionOpen && !canEditClasses && (
                <p className="mt-2 text-[12px] text-gray-500 text-center">
                  {T.classSelectionClosed}
                </p>
              )}
            </section>
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {editingClasses && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200"
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <h2 className="text-[18px] font-semibold text-brand-navy">{T.editClassTitle}</h2>
                  <button onClick={() => setEditingClasses(false)} className="p-2 rounded-full hover:bg-gray-100 text-gray-400 transition-colors">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                {classesLoading ? (
                  <p className="text-[14px] text-gray-400">{T.loadingClasses}</p>
                ) : classesError ? (
                  <p className="text-[14px] font-medium text-brand-maroon mb-3">{classesError}</p>
                ) : (
                  <>
                    <p className="text-[13px] text-gray-400 mb-3">{T.classesHint}</p>
                    <button onClick={() => setLocalSelectedIds([])} className={`w-full mb-3 p-3 rounded-xl border text-center transition-all ${localSelectedIds.length === 0 ? "border-brand-navy bg-brand-navy/5" : "border-gray-200 bg-white hover:border-brand-maroon/40"}`}>
                      <span className="text-[13px] font-medium text-gray-400">{T.selectNone}</span>
                    </button>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[320px] overflow-y-auto mb-4">
                      {sortedForumClasses.filter((cls) => !cls.externalLink && !cls.externalEmail).map((cls) => {
                        const cap = cls.maxCapacity ?? cls.cupos;
                        const isFull = cap != null && cap > 0 && (cls.currentEnrollment ?? 0) >= cap;
                        const isSelected = localSelectedIds.includes(cls.id);
                        const name = cls.nameEs ?? cls.nameEn ?? cls.name;
                        return (
                          <button key={cls.id} onClick={() => toggleClassInEditor(cls.id)} disabled={isFull && !isSelected} className={`p-3 rounded-xl border text-left transition-all ${isFull && !isSelected ? "border-gray-100 bg-gray-50 opacity-75 cursor-not-allowed" : isSelected ? "border-brand-navy bg-brand-navy/5" : "border-gray-200 hover:border-brand-maroon/40 bg-white"}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <span className="text-[13px] font-medium text-brand-navy line-clamp-2 block">{name}</span>
                                {(cls.monthlyPrice ?? 0) > 0 && <p className="text-[12px] text-gray-400 mt-1">RD$ {(cls.monthlyPrice ?? 0).toLocaleString()}{T.perMonth}</p>}
                              </div>
                              <div className="flex flex-col items-end shrink-0 gap-1">
                                {isSelected ? <span className="w-5 h-5 rounded-full bg-brand-navy flex items-center justify-center"><span className="text-white text-[10px]">✓</span></span> : <span className="w-5 h-5 rounded-full border-2 border-gray-200" />}
                                {cap != null && cap > 0 && <span className="text-[11px] text-gray-400 tabular-nums">{(cls.currentEnrollment ?? 0)}/{cap}</span>}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="flex gap-3">
                        <button onClick={saveClassSelection} disabled={savingClasses || (localSelectedIds.length === selectedClassIds.length && localSelectedIds.every((id) => selectedClassIds.includes(id)))} className="flex-1 py-3 rounded-xl font-semibold text-[14px] text-white bg-brand-navy hover:opacity-95 disabled:opacity-50 transition-opacity">
                          {savingClasses ? T.saving : T.saveSelection}
                        </button>
                        <button onClick={() => setEditingClasses(false)} className="px-4 py-3 rounded-xl font-semibold text-[14px] text-gray-400 hover:bg-gray-100 transition-colors">{T.cancel}</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {classDetailId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-gray-200">
              <div className="p-6">
                <div className="flex items-start justify-between gap-2 mb-4">
                  <h2 className="text-[18px] font-semibold text-brand-navy">{T.classDetailTitle}</h2>
                  <button onClick={() => setClassDetailId(null)} className="p-2 rounded-full hover:bg-gray-100 text-gray-400 transition-colors">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                {classDetailLoading ? <p className="text-[14px] text-gray-400">{T.loading}</p> : (() => {
                  const current = currentClasses.find((c) => c.id === classDetailId);
                  const full = forumClasses.find((c) => c.id === classDetailId);
                  const name = full?.nameEs ?? full?.nameEn ?? full?.name ?? current?.name ?? classDetailId;
                  const price = full?.monthlyPrice ?? 0;
                  const scheduleDay = full?.scheduleDay ?? full?.scheduleEs ?? full?.scheduleEn;
                  const scheduleTime = full?.scheduleTime ?? full?.time;
                  const cap = full?.maxCapacity ?? full?.cupos;
                  const enrolled = full?.currentEnrollment ?? 0;
                  return (
                    <div className="space-y-4">
                      <p className="text-[16px] font-medium text-brand-navy">{name}</p>
                      {price > 0 && <p className="text-[14px] text-gray-400">RD$ {price.toLocaleString("es-DO")}{T.perMonth}</p>}
                      {(scheduleDay || scheduleTime) && <p className="text-[14px] text-gray-400">{[scheduleDay, scheduleTime].filter(Boolean).join(" · ")}</p>}
                      {cap != null && cap > 0 && <p className="text-[14px] text-gray-400">{T.spots} {enrolled}/{cap}</p>}
                      <p className="text-[14px] text-gray-400">{T.paymentFrequency} {paymentFrequency === "BIMESTRAL" ? T.freqBimonthlyLabel : T.freqQuarterlyLabel}</p>
                      <button onClick={() => setClassDetailId(null)} className="w-full py-3 rounded-xl font-semibold text-[14px] text-gray-400 hover:bg-gray-100 transition-colors border border-gray-200">{T.close}</button>
                    </div>
                  );
                })()}
              </div>
            </motion.div>
          </div>
        )}

        {switchToOnlineModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-200">
              <div className="p-6">
                <h2 className="text-[18px] font-semibold text-brand-navy mb-4">{T.switchOnlineTitle}</h2>
                <p className="text-[14px] text-gray-600 mb-4">
                  {T.switchOnlineBody}
                </p>
                {switchPaymentError && (
                  <p className="text-[14px] font-medium text-brand-maroon mb-3">{switchPaymentError}</p>
                )}
                <div className="flex flex-col gap-3">
                  <button onClick={handleSwitchToOnlineYes} disabled={switchingPaymentMethod} className="w-full py-3.5 rounded-xl font-semibold text-[14px] text-white bg-brand-navy hover:opacity-95 transition-opacity disabled:opacity-50">
                    {switchingPaymentMethod ? T.switching : T.switchYes}
                  </button>
                  <button onClick={handleSwitchToOnlineNo} disabled={switchingPaymentMethod} className="w-full py-3.5 rounded-xl font-semibold text-[14px] border border-gray-200 bg-white text-brand-navy hover:bg-gray-50 transition-colors">
                    {T.no}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {payModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-200">
              <div className="p-6">
                <h2 className="text-[18px] font-semibold text-brand-navy mb-4">{T.fiscalTitle}</h2>
                {comprobanteChoice === "ask" && (
                  <div className="flex flex-col gap-3">
                    <button onClick={() => setComprobanteChoice("yes")} className="w-full py-3.5 rounded-xl font-semibold text-[14px] border border-gray-200 bg-gray-50 text-brand-navy hover:bg-gray-100 transition-colors">{T.fiscalYes}</button>
                    <button onClick={handleComprobanteNo} className="w-full py-3.5 rounded-xl font-semibold text-[14px] border border-gray-200 bg-gray-50 text-brand-navy hover:bg-gray-100 transition-colors">{T.fiscalNo}</button>
                    <button onClick={() => setPayModalOpen(false)} className="mt-2 text-[13px] font-medium text-gray-400 hover:text-brand-navy">{T.fiscalClose}</button>
                  </div>
                )}
                {comprobanteChoice === "yes" && (
                  <div className="space-y-4 text-left">
                    <p className="text-[12px] text-gray-500">
                      {T.fiscalB01Note}
                    </p>
                    <div>
                      <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{T.fiscalName}</label>
                      <input type="text" value={fiscalForm.fiscalName} onChange={(e) => setFiscalForm((f) => ({ ...f, fiscalName: e.target.value }))} placeholder={T.fiscalNamePlaceholder} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-[14px] text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/20" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{T.documentType}</label>
                        <select value={fiscalForm.documentType} onChange={(e) => setFiscalForm((f) => ({ ...f, documentType: e.target.value as DocumentType }))} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-[14px] text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/20">
                          <option value="RNC">RNC</option>
                          <option value="CEDULA">{lang === "es" ? "Cédula" : "ID Card"}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{T.documentNumber}</label>
                        <input type="text" value={fiscalForm.documentNumber} onChange={(e) => setFiscalForm((f) => ({ ...f, documentNumber: e.target.value }))} placeholder={T.documentNumberPlaceholder} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-[14px] text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-navy/20" />
                      </div>
                    </div>
                    {fiscalError && <p className="text-[13px] font-medium text-brand-maroon">{fiscalError}</p>}
                    <div className="flex flex-col gap-2 pt-2">
                      <button onClick={handleComprobanteYesContinue} disabled={savingFiscal} className="w-full py-3.5 rounded-xl font-semibold text-[14px] text-white bg-brand-navy hover:opacity-95 disabled:opacity-70 transition-opacity">
                        {savingFiscal ? T.saving : T.continueToPay}
                      </button>
                      <button onClick={() => setComprobanteChoice("ask")} className="text-[13px] font-medium text-gray-400 hover:text-brand-navy">{T.backInModal}</button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}